import {
  AffiliateReferral,
  AffiliateStats,
  AffiliatesService,
  CreateAffiliateDto,
  InviteAffiliateDto,
  UpdateAffiliateDto,
} from "@/lib/api/affiliates.service";
import {
  AffiliateNote,
  AffiliateTransaction,
  AffiliateUser,
  StripeSubscriptionPlan,
} from "@/lib/schema";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "./use-toast";

export const useAffiliates = () => {
  return useQuery<AffiliateUser[]>({
    queryKey: ["/affiliate"],
    queryFn: () => AffiliatesService.getAffiliates(),
  });
};

export const useAffiliate = (id: string | undefined) => {
  return useQuery<AffiliateUser>({
    queryKey: ["/affiliate", id],
    queryFn: () => AffiliatesService.getAffiliate(id!),
    enabled: !!id,
  });
};

export const useCreateAffiliate = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ data, file }: { data: CreateAffiliateDto; file?: File }) =>
      AffiliatesService.createAffiliate(data, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/affiliate"] });
      toast({
        title: "Affiliate created",
        description: "The new affiliate account has been created successfully.",
      });
      onSuccess?.();
    },
    onError: (error: any) => {
      toast({
        title: "Error creating affiliate",
        description: error.message || "Failed to create affiliate.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateAffiliate = (id: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ data, file }: { data: UpdateAffiliateDto; file?: File }) =>
      AffiliatesService.updateAffiliate(id, data, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/affiliate"] });
      queryClient.invalidateQueries({ queryKey: ["/affiliate", id] });
      toast({
        title: "Affiliate updated",
        description: "The affiliate account has been updated successfully.",
      });
      onSuccess?.();
    },
    onError: (error: any) => {
      toast({
        title: "Error updating affiliate",
        description: error.message || "Failed to update affiliate.",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteAffiliate = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => AffiliatesService.deleteAffiliate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/affiliate"] });
      toast({
        title: "Affiliate deleted",
        description: "The affiliate account has been soft-deleted.",
        variant: "destructive",
      });
      onSuccess?.();
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting affiliate",
        description: error.message || "Failed to delete affiliate.",
        variant: "destructive",
      });
    },
  });
};

export const useRestoreAffiliate = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => AffiliatesService.restoreAffiliate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/affiliate"] });
      toast({
        title: "Affiliate restored",
        description: "The affiliate account has been restored.",
      });
      onSuccess?.();
    },
    onError: (error: any) => {
      toast({
        title: "Error restoring affiliate",
        description: error.message || "Failed to restore affiliate.",
        variant: "destructive",
      });
    },
  });
};

export function useSetAffiliateCommission(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      userId,
      commissionRate,
    }: {
      userId: string;
      commissionRate: number;
    }) => AffiliatesService.setCommission(userId, commissionRate),

    onSuccess: (_, variables) => {
      toast({
        title: "Commission updated",
        description: "Affiliate commission has been updated successfully.",
      });

      // Refresh affiliate data
      queryClient.invalidateQueries({ queryKey: ["/affiliate"] });
      queryClient.invalidateQueries({
        queryKey: ["/affiliate", variables.userId],
      });

      onSuccess?.();
    },

    onError: (error: any) => {
      toast({
        title: "Update failed",
        description:
          error?.message || "Failed to update commission. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export const useSuspendAffiliate = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => AffiliatesService.suspendAffiliate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/affiliate"] });
      toast({
        title: "Affiliate suspended",
        description: "The affiliate account has been suspended.",
        variant: "destructive",
      });
      onSuccess?.();
    },
    onError: (error: any) => {
      toast({
        title: "Error suspending affiliate",
        description: error.message || "Failed to suspend affiliate.",
        variant: "destructive",
      });
    },
  });
};

export const useInviteAffiliate = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: InviteAffiliateDto) =>
      AffiliatesService.inviteAffiliate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/affiliate"] });
      toast({
        title: "Invitation sent",
        description: "The invitation has been sent successfully.",
      });
      onSuccess?.();
    },
    onError: (error: any) => {
      toast({
        title: "Error inviting affiliate",
        description: error.message || "Failed to send invitation.",
        variant: "destructive",
      });
    },
  });
};

export const useAffiliateNotes = (affiliateId: string | undefined) => {
  return useQuery<AffiliateNote[]>({
    queryKey: ["/affiliate", affiliateId, "notes"],
    queryFn: () => AffiliatesService.getNotes(affiliateId!),
    enabled: !!affiliateId,
  });
};

export const useAddAffiliateNote = (
  affiliateId: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (content: string) =>
      AffiliatesService.addNote(affiliateId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/affiliate", affiliateId, "notes"],
      });
      toast({
        title: "Note added",
        description: "The support note has been added successfully.",
      });
      onSuccess?.();
    },
    onError: (error: any) => {
      toast({
        title: "Error adding note",
        description: error.message || "Failed to add note.",
        variant: "destructive",
      });
    },
  });
};

export const useAffiliateStats = (affiliateId: string | undefined) => {
  return useQuery<AffiliateStats>({
    queryKey: ["/affiliate", affiliateId, "stats"],
    queryFn: () => AffiliatesService.getAffiliateStats(affiliateId!),
    enabled: !!affiliateId,
  });
};

export const useAffiliateReferrals = (affiliateId: string | undefined) => {
  return useQuery<AffiliateReferral[]>({
    queryKey: ["/affiliate", affiliateId, "referrals"],
    queryFn: () => AffiliatesService.getReferrals(affiliateId!),
    enabled: !!affiliateId,
  });
};

export const useAffiliateTransactions = (affiliateId: string | undefined) => {
  return useQuery<AffiliateTransaction[]>({
    queryKey: ["/affiliate", affiliateId, "transactions"],
    queryFn: () => AffiliatesService.getTransactions(affiliateId!),
    enabled: !!affiliateId,
  });
};

export const useSendAffiliateReport = (onSuccess?: () => void) => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      affiliateId,
      options,
    }: {
      affiliateId: string;
      options?: {
        startDate?: string;
        endDate?: string;
        format?: "html" | "csv";
      };
    }) => AffiliatesService.sendReport(affiliateId, options),
    onSuccess: (data) => {
      toast({
        title: "Report sent",
        description: `The affiliate report has been sent to ${data.sentTo}`,
      });
      onSuccess?.();
    },
    onError: (error: any) => {
      toast({
        title: "Error sending report",
        description: error.message || "Failed to send affiliate report.",
        variant: "destructive",
      });
    },
  });
};

export const useSubscriptionPlans = () => {
  return useQuery<StripeSubscriptionPlan[]>({
    queryKey: ["/subscription/plans"],
    queryFn: () => AffiliatesService.getSubscriptionPlans(),
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};
